/**
 * 短縮アドレス (イベント配布用の期間限定アドレス) の共有型と純粋ヘルパー。
 *
 * 背景 (2026-09-05 オーナー要望):
 *   イベント会場では、参加者が **PC のアドレスバーに手で打ち込む** ことがある。参加 URL の正規形
 *   `{origin}/m/{urlToken}` は urlToken が 32 文字以上 (docs/13 §3-2 で推測困難性を担保) のため
 *   手入力に耐えない。そこで「短くて打ちやすい」別入口 `{origin}/s/{code}` を **期間限定** で発行し、
 *   期限が切れても **同じアドレスのまま再有効化** できるようにする。
 *
 * 設計の要点:
 *   - `shortLinks/{code}` (code = doc ID) は **期限切れでも削除しない**。だから再有効化すれば
 *     同じアドレスがそのまま復活し、印刷済みのチラシ・掲示物を刷り直さなくてよい。
 *   - 短いコードは総当りできてしまうため、**期限必須 (最大 360 日)** + 停止 (disabledAt) +
 *     解決 callable のレート制限で守る。恒久的な入口はあくまで長い urlToken 側。
 *   - 解決先は「地区」または「地区 + セッション」。セッション付きは参加 QR (`/m/:urlToken/join/:sessionId`)
 *     と同じ入口に着地するので、配布物の種類 (QR / 短縮アドレス) で参加者の体験が変わらない。
 *
 * 本ファイルは型 + pure 関数のみ (apps / functions が単一の真実源として import する)。
 * Firestore doc の server 内部表現 (Timestamp 付き) は functions/src/shortlink/types.ts。
 */
/** Firestore コレクション名 (doc ID = 正規化済み code)。 */
export const SHORT_LINK_COLLECTION = 'shortLinks';
/** 短縮アドレスの URL パス接頭辞 (`{origin}/s/{code}`)。user アプリの route と一致させる。 */
export const SHORT_LINK_PATH_PREFIX = '/s/';
/** 有効期間の下限 (日)。 */
export const SHORT_LINK_MIN_EXPIRY_DAYS = 1;
/** 有効期間の上限 (日)。オーナー確定要件「最大 360 日」。 */
export const SHORT_LINK_MAX_EXPIRY_DAYS = 360;
/** 有効期間の既定 (日、未指定 / 不正値のとき)。 */
export const SHORT_LINK_DEFAULT_EXPIRY_DAYS = 30;
/** UI の期間プリセット (日)。最後は上限 360。 */
export const SHORT_LINK_EXPIRY_PRESET_DAYS = [1, 3, 7, 14, 30, 60, 90, 180, 360];
/** 自分で決めるアドレス (vanity code) の最小長。総当りと打ち間違いを避けるため 4 文字以上。 */
export const SHORT_CODE_MIN_LENGTH = 4;
/** アドレスの最大長 (打ちやすさの上限。これを超えるなら通常の参加 URL でよい)。 */
export const SHORT_CODE_MAX_LENGTH = 32;
/** 自動生成コードの長さ。31^6 ≈ 8.9 億通りで、レート制限下では総当り不可。 */
export const SHORT_CODE_GENERATED_LENGTH = 6;
/**
 * 自動生成に使う文字集合。読み間違い・打ち間違いの多い `0 o 1 l i` を除外した 31 文字。
 * (掲示物を見て打ち込む場面を想定した選定。手入力の vanity code は下記 pattern の範囲で自由)
 */
export const SHORT_CODE_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';
/**
 * 入力されたアドレスを doc ID 用に正規化する。
 *
 * - 全角英数字 / 全角ハイフンを半角へ (日本語 IME のまま打たれることを想定、NFKC)
 * - 前後の空白と `/` を落とす (URL からコピペされた `/s/abc` や `abc/` も拾う)
 * - 小文字化 (大文字で掲示されても届くようにするため、照合は常に小文字)
 */
export function normalizeShortCode(raw) {
    const s = typeof raw === 'string' ? raw : '';
    return s.normalize('NFKC').trim().replace(/^\/+/, '').replace(/\/+$/, '').toLowerCase();
}
/**
 * 正規化済みアドレスの妥当性検査 (pure)。
 * 使える文字は英小文字・数字・ハイフン。先頭末尾のハイフンは不可 (打ち間違いの温床)。
 */
export function validateShortCode(code) {
    if (code.length < SHORT_CODE_MIN_LENGTH)
        return { ok: false, reason: 'too-short' };
    if (code.length > SHORT_CODE_MAX_LENGTH)
        return { ok: false, reason: 'too-long' };
    if (!/^[a-z0-9-]+$/.test(code))
        return { ok: false, reason: 'charset' };
    if (code.startsWith('-') || code.endsWith('-'))
        return { ok: false, reason: 'edge-hyphen' };
    return { ok: true };
}
/** 不受理理由の日本語文言 (UI 用。内部キーを画面に出さないルールに従う)。 */
export function shortCodeErrorMessage(reason) {
    switch (reason) {
        case 'too-short':
            return `アドレスは ${SHORT_CODE_MIN_LENGTH} 文字以上にしてください`;
        case 'too-long':
            return `アドレスは ${SHORT_CODE_MAX_LENGTH} 文字以内にしてください`;
        case 'charset':
            return 'アドレスに使えるのは半角の英小文字・数字・ハイフンだけです';
        case 'edge-hyphen':
            return 'アドレスの最初と最後にハイフンは使えません';
    }
}
/** 有効日数を 1〜360 に丸める (未指定 / 不正値は既定 30)。 */
export function clampShortLinkExpiryDays(days) {
    const n = typeof days === 'number' && Number.isFinite(days)
        ? Math.round(days)
        : SHORT_LINK_DEFAULT_EXPIRY_DAYS;
    return Math.min(SHORT_LINK_MAX_EXPIRY_DAYS, Math.max(SHORT_LINK_MIN_EXPIRY_DAYS, n));
}
/**
 * 状態の導出 (pure)。手動停止が最優先 = 期限内でも停止中なら使えない。
 * `expiresAt` / `disabledAt` は ISO8601 か epoch ms。
 */
export function shortLinkStatus(link, nowMs = Date.now()) {
    if (link.disabledAt)
        return 'disabled';
    const expires = typeof link.expiresAt === 'number' ? link.expiresAt : Date.parse(link.expiresAt);
    if (!Number.isFinite(expires))
        return 'expired';
    return expires > nowMs ? 'active' : 'expired';
}
/** 残り日数 (切り上げ)。期限切れは 0。UI の「あと N 日」表示用。 */
export function shortLinkRemainingDays(expiresAt, nowMs = Date.now()) {
    const expires = typeof expiresAt === 'number' ? expiresAt : Date.parse(expiresAt);
    if (!Number.isFinite(expires))
        return 0;
    const diff = expires - nowMs;
    return diff <= 0 ? 0 : Math.ceil(diff / (24 * 60 * 60 * 1000));
}
/** 配布用の完全な短縮 URL (`{origin}/s/{code}`)。 */
export function buildShortLinkUrl(origin, code) {
    const safeOrigin = origin.replace(/\/+$/, '');
    return `${safeOrigin}${SHORT_LINK_PATH_PREFIX}${code}`;
}
/**
 * 「参加者用の URL と QR コード」に併記する短縮アドレスを選ぶ (pure)。
 *
 * 条件: 併記の指定あり + 配布対象 (全体共通 / セッション) が一致 + 今この時点で有効
 * (期限内かつ停止していない)。status はサーバーの一覧取得時点の値なので、画面を開いたまま
 * 期限を跨いだ場合に備えて `expiresAt` / `disabledAt` から再判定する。
 * 該当が複数なら最も新しく発行したもの (掲示物を刷り直したら新しい方が正のため)。
 */
export function pickParticipantQrShortLink(links, sessionId, nowMs = Date.now()) {
    const target = sessionId || null;
    const candidates = links.filter((l) => l.showOnParticipantQr === true &&
        (l.sessionId || null) === target &&
        shortLinkStatus(l, nowMs) === 'active');
    if (candidates.length === 0)
        return null;
    return [...candidates].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0];
}
/**
 * 解決後の遷移先パス。セッション指定があれば参加 QR と同じ入口
 * (`/m/:urlToken/join/:sessionId`) に着地させる。
 */
export function buildShortLinkTargetPath(urlToken, sessionId) {
    const base = `/m/${encodeURIComponent(urlToken)}`;
    return sessionId ? `${base}/join/${encodeURIComponent(sessionId)}` : base;
}
/** 使えない短縮アドレスの案内文言 (UI 用、内部キーは出さない)。 */
export function shortLinkUnavailableMessage(reason) {
    switch (reason) {
        case 'expired':
            return 'この短縮アドレスは有効期間が終了しています。配布元にお問い合わせください。';
        case 'disabled':
            return 'この短縮アドレスは現在停止されています。配布元にお問い合わせください。';
        case 'district-archived':
            return 'この短縮アドレスの地区は現在利用できません。';
        case 'not-found':
            return 'この短縮アドレスは見つかりませんでした。入力に誤りがないかご確認ください。';
    }
}
//# sourceMappingURL=shortLink.js.map